import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  assignActivityGear,
  getActivity,
  listGear,
  listTags,
  removeActivityGear,
  setActivityTags,
  updateActivityEffort,
  updateActivityNotes,
} from "../api/endpoints";
import { mediaDisplayUrl, mediaFullUrl } from "../api/types";
import { Badge, Button, Card, EmptyState, ErrorMessage, PageHeading, Select, Spinner, Textarea } from "../components/ui";
import { useAsync } from "../hooks/useAsync";

function metric(label: string, value: string) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</dt>
      <dd className="mt-1 text-sm font-medium text-gray-900 dark:text-gray-100">{value}</dd>
    </div>
  );
}

export default function ActivityDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data, error, loading, reload } = useAsync(
    async () => {
      if (!id) throw new Error("Activity id is required");
      const [activity, tags, gear] = await Promise.all([getActivity(id), listTags(), listGear({ includeRetired: true })]);
      return { activity, tags: tags.tags, gear: gear.gear };
    },
    [id],
  );

  const [notes, setNotes] = useState("");
  const [effort, setEffort] = useState("");
  const [selectedTag, setSelectedTag] = useState("");
  const [selectedGear, setSelectedGear] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (!data) return;
    setNotes(data.activity.userNotes ?? "");
    setEffort(data.activity.perceivedEffort != null ? String(data.activity.perceivedEffort) : "");
  }, [data]);

  const availableTagOptions = useMemo(
    () => (data ? data.tags.filter((tag) => !data.activity.tags.includes(tag.slug)) : []),
    [data],
  );
  const availableGearOptions = useMemo(
    () => (data ? data.gear.filter((item) => !data.activity.gear.some((used) => used.id === item.id)) : []),
    [data],
  );

  async function mutate(action: string, fn: () => Promise<unknown>) {
    setSaveError(null);
    setBusy(action);
    try {
      await fn();
      reload();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }

  if (loading) return <Spinner />;
  if (error || !data) return <ErrorMessage error={error ?? new Error("Activity not found")} onRetry={reload} />;

  const activity = data.activity;

  return (
    <div className="space-y-6" data-testid="activity-detail-page">
      <Link to="/activities" className="text-sm text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100" data-testid="activity-detail-back-link">
        ← Back to timeline
      </Link>

      <PageHeading title={activity.activityName} subtitle={`${activity.activityType} · ${activity.activityDate}`} />
      {saveError && <ErrorMessage error={new Error(saveError)} />}

      <div className="grid gap-6 lg:grid-cols-[1.4fr_1fr]">
        <div className="space-y-6">
          <Card className="p-6">
            <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="activity-detail-metrics-panel">
              {metric("Distance", activity.distanceMeters != null ? `${(activity.distanceMeters / 1000).toFixed(2)} km` : "—")}
              {metric("Duration", activity.durationMinutes != null ? `${activity.durationMinutes} min` : "—")}
              {metric("Moving time", activity.movingTimeSeconds != null ? `${Math.round(activity.movingTimeSeconds / 60)} min` : "—")}
              {metric("Average HR", activity.averageHeartrate != null ? `${activity.averageHeartrate} bpm` : "—")}
              {metric("Max HR", activity.maxHeartrate != null ? `${activity.maxHeartrate} bpm` : "—")}
              {metric("Elevation gain", activity.elevationGainM != null ? `${activity.elevationGainM} m` : "—")}
              {metric("Elevation loss", activity.elevationLossM != null ? `${activity.elevationLossM} m` : "—")}
              {metric("Average cadence", activity.avgCadence != null ? `${activity.avgCadence} spm` : "—")}
              {metric("Calories", activity.calories != null ? String(activity.calories) : "—")}
            </dl>
            <div className="mt-4 flex flex-wrap gap-2">
              {activity.intensityLevel && <Badge>{activity.intensityLevel}</Badge>}
              {activity.runCategory && <Badge>{activity.runCategory}</Badge>}
              {activity.isCommute && <Badge>Commute</Badge>}
              {activity.deviceName && <Badge>{activity.deviceName}</Badge>}
            </div>
            {activity.description && <p className="mt-4 text-sm text-gray-600 dark:text-gray-300">{activity.description}</p>}
          </Card>

          <Card className="p-6">
            <h2 className="text-heading-md text-gray-900 dark:text-gray-100">Notes</h2>
            <Textarea rows={5} value={notes} onChange={(event) => setNotes(event.target.value)} className="mt-3" data-testid="activity-detail-notes-input" />
            <div className="mt-3 flex justify-end">
              <Button onClick={() => void mutate("notes", () => updateActivityNotes(activity.id, notes || null))} disabled={busy !== null} data-testid="activity-detail-notes-save-button">{busy === "notes" ? "Saving…" : "Save notes"}</Button>
            </div>
          </Card>

          <Card className="p-6">
            <h2 className="text-heading-md text-gray-900 dark:text-gray-100">Photos & media</h2>
            {activity.media.length === 0 ? (
              <EmptyState title="No media linked" description="When an activity has imported photos they will appear here." testId="activity-detail-media-empty-state" />
            ) : (
              <div className="mt-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-3" data-testid="activity-detail-media-grid">
                {activity.media.map((item) => (
                  <a key={item.mediaId} href={mediaFullUrl(item.mediaId)} target="_blank" rel="noreferrer" className="overflow-hidden rounded-lg border border-gray-200 dark:border-[#2e2e2e]" data-testid="activity-detail-media-link">
                    <img src={mediaDisplayUrl(item.mediaId)} alt={item.caption ?? activity.activityName} className="h-48 w-full object-cover" />
                    <div className="p-3 text-sm text-gray-600 dark:text-gray-300">
                      <p>{item.caption ?? item.mediaType}</p>
                    </div>
                  </a>
                ))}
              </div>
            )}
          </Card>
        </div>

        <div className="space-y-6">
          <Card className="p-6">
            <h2 className="text-heading-md text-gray-900 dark:text-gray-100">Perceived effort</h2>
            <Select value={effort} onChange={(event) => setEffort(event.target.value)} className="mt-3" data-testid="activity-detail-effort-input">
              <option value="">Not recorded</option>
              {Array.from({ length: 10 }, (_, index) => index + 1).map((value) => (
                <option key={value} value={value}>{value}</option>
              ))}
            </Select>
            <div className="mt-3 flex justify-end">
              <Button onClick={() => void mutate("effort", () => updateActivityEffort(activity.id, effort ? Number(effort) : null))} disabled={busy !== null} data-testid="activity-detail-effort-save-button">{busy === "effort" ? "Saving…" : "Save effort"}</Button>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-heading-md text-gray-900 dark:text-gray-100">Tags</h2>
              <Badge>{activity.tags.length} assigned</Badge>
            </div>
            <div className="mt-3 flex flex-wrap gap-2" data-testid="activity-detail-tags-panel">
              {activity.tags.length === 0 && <p className="text-sm text-gray-500 dark:text-gray-400">No tags assigned yet.</p>}
              {activity.tags.map((tag) => (
                <span key={tag} className="inline-flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1 text-sm text-gray-700 dark:bg-[#252525] dark:text-gray-200">
                  {tag}
                  <button type="button" onClick={() => void mutate(`remove-tag-${tag}`, () => setActivityTags(activity.id, activity.tags.filter((value) => value !== tag)))} className="text-amber-700 dark:text-amber-300" data-testid="activity-detail-tag-remove-button">×</button>
                </span>
              ))}
            </div>
            <div className="mt-4 flex gap-2">
              <Select value={selectedTag} onChange={(event) => setSelectedTag(event.target.value)} data-testid="activity-detail-tag-select">
                <option value="">Select a tag</option>
                {availableTagOptions.map((tag) => (
                  <option key={tag.slug} value={tag.slug}>{tag.display}</option>
                ))}
              </Select>
              <Button disabled={!selectedTag || busy !== null} onClick={() => void mutate("tag", async () => { await setActivityTags(activity.id, [...activity.tags, selectedTag]); setSelectedTag(""); })} data-testid="activity-detail-tag-add-button">Add</Button>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-heading-md text-gray-900 dark:text-gray-100">Gear used</h2>
              <Badge>{activity.gear.length} linked</Badge>
            </div>
            <div className="mt-3 space-y-2" data-testid="activity-detail-gear-panel">
              {activity.gear.length === 0 && <p className="text-sm text-gray-500 dark:text-gray-400">No gear linked yet.</p>}
              {activity.gear.map((item) => (
                <div key={item.id} className="flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2 dark:border-[#2e2e2e]">
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{item.name}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{item.gearType}{item.brand ? ` · ${item.brand}` : ""}</p>
                  </div>
                  <button type="button" onClick={() => void mutate(`remove-gear-${item.id}`, () => removeActivityGear(activity.id, item.id))} className="text-sm font-medium text-amber-700 dark:text-amber-300" data-testid="activity-detail-gear-remove-button">Remove</button>
                </div>
              ))}
            </div>
            <div className="mt-4 flex gap-2">
              <Select value={selectedGear} onChange={(event) => setSelectedGear(event.target.value)} data-testid="activity-detail-gear-select">
                <option value="">Select gear</option>
                {availableGearOptions.map((item) => (
                  <option key={item.id} value={item.id}>{item.name}</option>
                ))}
              </Select>
              <Button disabled={!selectedGear || busy !== null} onClick={() => void mutate("gear", async () => { await assignActivityGear(activity.id, selectedGear); setSelectedGear(""); })} data-testid="activity-detail-gear-add-button">Link</Button>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
